/**
 * The press as a ring of stations — Separation Studio's Press Fit picture, redrawn from the spec
 * (docs/screen-print-station-audit-and-plan-v1.md § 3.6). Display only: heads tinted by the ink's hex
 * with the mesh under the number, flash stations amber, empty stations dashed, a conflict outlined in
 * red. Renders identically in the editor (live, as heads are typed), the packet and the kiosk; the
 * job ticket prints it in B&W.
 *
 * SVG rather than CSS transforms so the geometry is one function (`stationAngle`) and the tiles stay
 * upright without a counter-rotation.
 *
 * `mono` is the paper variant (the job ticket, § 4.3): ink on white only — heads are white tiles with a
 * black rule, flash stations are HATCHED, an empty station is dashed and a conflict is a heavier rule.
 * No fill carries meaning, so nothing is lost on a B&W laser. The colour path is untouched by the flag.
 */
import { useId } from 'react';
import type { PressShape, RingScreen, StationTile } from './screen-print-spec-lib';
import { stationLayout } from './screen-print-spec-lib';

const SIZE = 320;
const CENTRE = SIZE / 2;
const RING_R = 92;
const TILE_R = 128;
const TILE_W = 46;
const TILE_H = 58;

/** Dark ink wants light text and vice versa — a plain luminance split is enough for a tile label. */
export function labelColourFor(hex: string | undefined): string {
  if (!hex) return '#111';
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#111' : '#fff';
}

/** The three colours a tile is drawn in, for the screen (ink tint) or for paper (ink on white). */
function tilePaint(
  tile: StationTile,
  mono: boolean,
  hatchId: string,
): { fill: string; stroke: string; text: string } {
  const hex = tile.screen?.hex;
  if (mono) {
    return {
      fill:
        tile.kind === 'flash' ? `url(#${hatchId})` : tile.kind === 'empty' ? 'transparent' : '#fff',
      stroke: '#000',
      text: '#000',
    };
  }
  return {
    fill:
      tile.kind === 'flash'
        ? '#f59e0b'
        : tile.kind === 'empty'
          ? 'transparent'
          : (hex ?? '#e5e5e5'),
    stroke: tile.kind === 'conflict' ? '#dc2626' : tile.kind === 'empty' ? '#a1a1aa' : '#fff',
    text:
      tile.kind === 'flash' ? '#1c1917' : tile.kind === 'empty' ? '#71717a' : labelColourFor(hex),
  };
}

function Tile({ tile, mono, hatchId }: { tile: StationTile; mono: boolean; hatchId: string }) {
  const rad = ((tile.angleDeg - 90) * Math.PI) / 180;
  const cx = CENTRE + TILE_R * Math.cos(rad);
  const cy = CENTRE + TILE_R * Math.sin(rad);
  const x = cx - TILE_W / 2;
  const y = cy - TILE_H / 2;
  const { fill, stroke, text } = tilePaint(tile, mono, hatchId);
  // On paper the flash label sits over the hatching, so it gets a white halo (paint-order: stroke).
  const halo =
    mono && tile.kind === 'flash' ? { stroke: '#fff', strokeWidth: 3, paintOrder: 'stroke' } : {};
  return (
    <g data-testid="station" data-station={tile.station} data-kind={tile.kind}>
      <title>
        {tile.kind === 'flash'
          ? `Station ${tile.station} · flash`
          : tile.kind === 'empty'
            ? `Station ${tile.station} · empty`
            : `Station ${tile.station} · ${tile.screen?.label ?? ''}${tile.kind === 'conflict' ? ' · conflict' : ''}`}
      </title>
      <rect
        x={x}
        y={y}
        width={TILE_W}
        height={TILE_H}
        rx={6}
        fill={fill}
        stroke={stroke}
        strokeWidth={tile.kind === 'conflict' ? (mono ? 3.5 : 3) : mono ? 1.5 : 2}
        strokeDasharray={tile.kind === 'empty' ? '4 3' : undefined}
        style={{
          filter: tile.kind === 'empty' || mono ? undefined : 'drop-shadow(0 0 0 rgba(0,0,0,.3))',
        }}
      />
      <text
        x={cx}
        y={tile.kind === 'flash' ? cy - 2 : cy + (tile.screen?.meshCount ? -2 : 5)}
        textAnchor="middle"
        fontSize={tile.kind === 'flash' ? 9 : 14}
        fontWeight={800}
        fill={text}
        style={{ letterSpacing: tile.kind === 'flash' ? '.04em' : undefined }}
        {...halo}
      >
        {tile.kind === 'flash' ? 'FLASH' : tile.station}
      </text>
      {tile.kind === 'flash' && (
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          fontSize={9}
          fontWeight={700}
          fill={text}
          {...halo}
        >
          st {tile.station}
        </text>
      )}
      {tile.kind !== 'flash' && tile.screen?.meshCount != null && (
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize={9} fontWeight={700} fill={text}>
          {tile.screen.meshCount}
        </text>
      )}
    </g>
  );
}

export function PressCarousel({
  press,
  screens,
  caption,
  className,
  mono = false,
}: {
  press: PressShape;
  screens: readonly RingScreen[];
  /** Two short lines in the hub — the press name and "head order = print order". */
  caption?: [string, string];
  className?: string;
  /** Ink on white for paper: no tint, hatched flash stations. */
  mono?: boolean;
}) {
  const layout = stationLayout(screens, press);
  // `useId` may contain `:` or `«»` depending on the React release — neither is safe inside `url(#…)`,
  // so the pattern id keeps only what every id syntax accepts. Unique per instance, so two mono rings
  // on one page never share (or shadow) a pattern.
  const hatchId = `carousel-hatch-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className={className}
      role="img"
      aria-label={`${press.stations}-station press carousel`}
      data-testid="press-carousel"
      data-mono={mono ? 'true' : undefined}
    >
      {mono && (
        <defs>
          <pattern
            id={hatchId}
            patternUnits="userSpaceOnUse"
            width={6}
            height={6}
            patternTransform="rotate(45)"
          >
            <line x1={0} y1={0} x2={0} y2={6} stroke="#000" strokeWidth={1.4} />
          </pattern>
        </defs>
      )}
      <circle
        cx={CENTRE}
        cy={CENTRE}
        r={RING_R}
        fill="none"
        stroke={mono ? '#000' : '#2fcf9c'}
        strokeWidth={mono ? 3 : 10}
      />
      {caption && (
        <>
          <text
            x={CENTRE}
            y={CENTRE - 4}
            textAnchor="middle"
            fontSize={11}
            fontWeight={700}
            fill={mono ? '#000' : '#525252'}
          >
            {caption[0]}
          </text>
          <text
            x={CENTRE}
            y={CENTRE + 11}
            textAnchor="middle"
            fontSize={9}
            fill={mono ? '#000' : '#737373'}
          >
            {caption[1]}
          </text>
        </>
      )}
      {layout.tiles.map((t) => (
        <Tile key={t.station} tile={t} mono={mono} hatchId={hatchId} />
      ))}
    </svg>
  );
}
